import React, { useMemo, useState, useEffect } from 'react';
import { Copy, Search, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { resolveTimezone, getZonedParts } from '../../utils/timezone';

const usesOdo = (b) => b === 'odometer' || b === 'odometer_calendar';
const usesHrs = (b) => b === 'engine_hours' || b === 'engine_hours_calendar';
const hasCal = (b) => b === 'calendar' || b === 'odometer_calendar' || b === 'engine_hours_calendar';

/** Clone one schedule to many assets, each with its own Last Serviced values. */
export const DuplicateScheduleDialog = ({ schedule, devices, onClose, onDone }) => {
  const { api } = useAuth();
  const tz = resolveTimezone();
  const [q, setQ] = useState('');
  const [rows, setRows] = useState({}); // device_id -> { checked, baseline_value, baseline_date }
  const [busy, setBusy] = useState(false);
  const basis = schedule?.basis;
  const metricOn = usesOdo(basis) || usesHrs(basis);
  const unit = usesOdo(basis) ? 'km' : 'hrs';

  const todayLocal = useMemo(() => {
    const p = getZonedParts(new Date(), { timezone: tz });
    return p ? `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}` : '';
  }, [tz]);

  useEffect(() => { setRows({}); setQ(''); }, [schedule?.id]);

  const candidates = useMemo(() => {
    const list = (devices || []).filter((d) => d.id !== schedule?.device_id)
      // km / hours schedules only make sense on vehicles; calendar-only can go to trailers & equipment too
      .filter((d) => !metricOn || (d._kind || 'vehicle') === 'vehicle');
    const s = q.trim().toLowerCase();
    return list
      .filter((d) => !s || `${d.asset_name || ''} ${d.name || ''} ${d.unit_number || ''}`.toLowerCase().includes(s))
      .sort((a, b) => (a.asset_name || a.name || '').localeCompare(b.asset_name || b.name || '', undefined, { sensitivity: 'base' }));
  }, [devices, schedule, q, metricOn]);

  const sourceOff = (d) => (usesOdo(basis) ? !['gps', 'sensor'].includes(d.odometer_source) : usesHrs(basis) ? !['ignition', 'sensor'].includes(d.engine_hours_source) : false);
  const current = (d) => (usesOdo(basis) ? d.computed_odometer : usesHrs(basis) ? d.computed_engine_hours : null);
  const label = (d) => d.asset_name || d.name || d.unit_number || d.id;

  const toggle = (d, checked) => setRows((r) => ({
    ...r,
    [d.id]: { ...(r[d.id] || { baseline_value: '', baseline_date: hasCal(basis) ? todayLocal : '' }), checked },
  }));
  const setVal = (id, key, v) => setRows((r) => ({ ...r, [id]: { ...(r[id] || { checked: true }), [key]: v } }));
  const selected = candidates.filter((d) => rows[d.id]?.checked && !sourceOff(d));
  const selectAll = (checked) => setRows((r) => {
    const next = { ...r };
    candidates.filter((d) => !sourceOff(d)).forEach((d) => { next[d.id] = { ...(next[d.id] || { baseline_value: '', baseline_date: hasCal(basis) ? todayLocal : '' }), checked }; });
    return next;
  });

  const submit = async () => {
    if (!selected.length) { toast.error('Select at least one asset'); return; }
    for (const d of selected) {
      const r = rows[d.id];
      if (metricOn && (r.baseline_value === '' || r.baseline_value == null)) { toast.error(`Enter the Last Serviced ${unit} for ${label(d)}`); return; }
      if (hasCal(basis) && !r.baseline_date) { toast.error(`Enter the Last Serviced Date for ${label(d)}`); return; }
    }
    setBusy(true);
    try {
      const res = await api.post(`/service/schedules/${schedule.id}/duplicate`, {
        targets: selected.map((d) => ({
          device_id: d.id,
          baseline_value: metricOn ? Number(rows[d.id].baseline_value) : undefined,
          baseline_date: hasCal(basis) && rows[d.id].baseline_date ? new Date(`${rows[d.id].baseline_date}T00:00:00`).toISOString() : undefined,
        })),
      });
      const { created_count, skipped } = res.data;
      if (created_count) toast.success(`Schedule duplicated to ${created_count} asset${created_count === 1 ? '' : 's'}`);
      if (skipped?.length) toast.warning(`Skipped ${skipped.length}: ${skipped.map((s) => `${s.name || s.device_id} (${s.reason})`).join('; ')}`, { duration: 8000 });
      onDone?.(res.data);
      onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to duplicate schedule'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!schedule} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="w-[95vw] max-w-3xl max-h-[90vh] flex flex-col p-0" data-testid="service-duplicate-dialog">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="flex items-center gap-2"><Copy className="w-4 h-4" /> Duplicate Schedule</DialogTitle>
          <DialogDescription>
            Copy <b>{schedule?.task_name}</b> (every {schedule?.interval_value} {metricOn ? unit : 'days'}{schedule?.interval_days ? ` or ${schedule.interval_days} days` : ''}, {(schedule?.reminder_stages || []).length} reminder stage{(schedule?.reminder_stages || []).length === 1 ? '' : 's'}) to other assets. Enter each asset's own Last Serviced {metricOn ? `${unit}${hasCal(basis) ? ' and date' : ''}` : 'date'}.
          </DialogDescription>
        </DialogHeader>
        <div className="px-6 pb-2 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search assets…" className="h-9 pl-8" data-testid="service-duplicate-search" />
          </div>
          <Button type="button" variant="outline" size="sm" onClick={() => selectAll(true)} data-testid="service-duplicate-select-all">Select all</Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => selectAll(false)} data-testid="service-duplicate-clear">Clear</Button>
          <span className="text-xs text-muted-foreground whitespace-nowrap" data-testid="service-duplicate-count">{selected.length} selected</span>
        </div>
        <div className="flex-1 overflow-y-auto px-6 pb-2">
          <div className="rounded-md border divide-y">
            {candidates.map((d) => {
              const off = sourceOff(d);
              const r = rows[d.id] || {};
              const cur = current(d);
              const entered = r.baseline_value;
              const above = metricOn && cur != null && entered !== '' && entered != null && Number(entered) > Number(cur);
              return (
                <div key={d.id} className={`flex flex-wrap items-center gap-3 px-3 py-2 text-sm ${off ? 'opacity-60' : ''}`} data-testid={`service-duplicate-row-${d.id}`}>
                  <Checkbox checked={!!r.checked && !off} disabled={off} onCheckedChange={(c) => toggle(d, !!c)} data-testid={`service-duplicate-check-${d.id}`} />
                  <div className="min-w-[160px] flex-1">
                    <div className="font-medium truncate">{label(d)}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {off ? <span className="text-amber-600 dark:text-amber-400 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {usesOdo(basis) ? 'Odometer' : 'Engine hours'} source is off — skipped</span>
                        : metricOn && cur != null ? `Current: ${cur} ${unit}` : (d._kind && d._kind !== 'vehicle' ? d._kind : 'Vehicle')}
                    </div>
                  </div>
                  {r.checked && !off && metricOn && (
                    <div className="w-44">
                      <Label className="text-[10px]">Last Serviced ({unit}) *</Label>
                      <Input type="number" value={r.baseline_value ?? ''} onChange={(e) => setVal(d.id, 'baseline_value', e.target.value)} placeholder={`Reading at last service`} className={`h-8 text-xs ${above ? 'border-amber-400' : ''}`} data-testid={`service-duplicate-value-${d.id}`} />
                      {above && <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">Above current reading</p>}
                    </div>
                  )}
                  {r.checked && !off && hasCal(basis) && (
                    <div className="w-40">
                      <Label className="text-[10px]">Last Serviced Date *</Label>
                      <Input type="date" value={r.baseline_date ?? ''} onChange={(e) => setVal(d.id, 'baseline_date', e.target.value)} className="h-8 text-xs" data-testid={`service-duplicate-date-${d.id}`} />
                    </div>
                  )}
                </div>
              );
            })}
            {!candidates.length && <div className="px-3 py-6 text-center text-xs text-muted-foreground">No other assets available</div>}
          </div>
        </div>
        <DialogFooter className="px-6 py-4 border-t">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy} data-testid="service-duplicate-cancel">Cancel</Button>
          <Button size="sm" onClick={submit} disabled={busy || !selected.length} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="service-duplicate-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Copy className="w-4 h-4 mr-1" />} Duplicate to {selected.length || ''} asset{selected.length === 1 ? '' : 's'}
          </Button>
          {selected.length > 0 && <Badge variant="outline" className="hidden" />}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
